import type { RouteLocationNamedRaw, RouteLocationRaw, Router } from 'vue-router';
import { routeConstants } from '@/utils/constants';
import { DATA_BASE_URL } from '@/utils/platformLinks';

/**
 * A form's URLs, in one place (docs/forms/REVIEW.md F7, decision 136).
 *
 * The in-app ones are route locations, so a caller pushes them or resolves them to an `href`, and
 * the route table stays the one source of each path. The public form's is an absolute string,
 * because it is copied and sent to people outside the app.
 */

/**
 * The form builder, in the current study. The study's param is left for the router to take from
 * the current location, which is how every dashboard route reaches its siblings.
 */
export const builderRoute = (formIdentifier: string): RouteLocationNamedRaw => ({
  name: routeConstants.FORM_BUILDER.name,
  params: { identifier: formIdentifier }
});

/**
 * The form preview, in the current study: the entry as a respondent would fill it in, saving
 * nothing. Opened in a new tab through `router.resolve(previewRoute(id)).href`, so Close preview
 * can close the tab it opened in.
 */
export const previewRoute = (formIdentifier: string): RouteLocationNamedRaw => ({
  name: routeConstants.FORM_PREVIEW.name,
  params: { formIdentifier }
});

/**
 * The printable form. Opened in a new tab through `router.resolve(printRoute(id, studyId)).href`.
 * Its route sits outside the study, so the study travels in the query: a form identifier is only
 * unique within a study, and the print view reads the form in the one it was opened from.
 */
export const printRoute = (formIdentifier: string, studyId?: string): RouteLocationNamedRaw => ({
  name: routeConstants.PRINT_FORM.name,
  params: { formIdentifier },
  ...(studyId ? { query: { studyId } } : {})
});

/**
 * Opens one of the in-app locations above in a new tab, resolved against the current route so a
 * dashboard route keeps its study. The list and the builder open Preview and Print this way.
 */
export const openInNewTab = (router: Pick<Router, 'resolve'>, location: RouteLocationRaw) => {
  window.open(router.resolve(location).href, '_blank');
};

/** The public form's address, the one the builder copies and opens. */
export const publicFormUrl = (formIdentifier: string): string =>
  `${DATA_BASE_URL}${routeConstants.PUBLIC_FORM.path.replace(
    ':formIdentifier',
    encodeURIComponent(formIdentifier)
  )}`;
